import React, { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Calendar, User, Eye, AlertCircle } from "lucide-react";
import { trashService, DeletedCategory } from "../../services/trashService";
import { formatDateTime } from "../../utils/dateFormatter";
import TrashDetailModal from "../TrashDetailModal";
import "../../styles/components/deleted-categories.css";

const DeletedCategories: React.FC = () => {
  const [categories, setCategories] = useState<DeletedCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState<DeletedCategory | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trashService.getDeletedCategories(page, limit);
      setCategories(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleRestore = async (categoryId: number) => {
    try {
      await trashService.restoreCategory(categoryId);
      setCategories(categories.filter(category => category.id !== categoryId));
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục danh mục');
    }
  };

  const handlePermanentDelete = async (categoryId: number) => {
    try {
      await trashService.permanentDeleteCategory(categoryId);
      setCategories(categories.filter(category => category.id !== categoryId));
      setSelectedCategories(prev => prev.filter(id => id !== categoryId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn danh mục');
    }
  };

  const handleBulkRestore = async () => {
    try {
      await trashService.bulkRestoreCategories(selectedCategories);
      setCategories(categories.filter(category => !selectedCategories.includes(category.id)));
      setSelectedCategories([]);
      setTotal(prev => prev - selectedCategories.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục các danh mục');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await trashService.bulkDeleteCategories(selectedCategories);
      setCategories(categories.filter(category => !selectedCategories.includes(category.id)));
      setSelectedCategories([]);
      setTotal(prev => prev - selectedCategories.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn các danh mục');
    }
  };

  const toggleSelectCategory = (categoryId: number) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const selectAll = () => {
    setSelectedCategories(categories.map(category => category.id));
  };

  const deselectAll = () => {
    setSelectedCategories([]);
  };

  const handleViewDetails = (category: DeletedCategory) => {
    setSelectedCategory(category);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedCategory(null);
  };

  if (loading) {
    return (
      <div className="trash-loading">
        <div className="trash-loading__spinner"></div>
        <p>Đang tải danh sách danh mục đã xóa...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-empty">
        <AlertCircle size={48} className="trash-empty__icon" />
        <h3 className="trash-empty__title">Lỗi tải dữ liệu</h3>
        <p className="trash-empty__description">{error}</p>
        <button onClick={loadCategories} className="trash-empty__retry">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="deleted-categories">
      <div className="deleted-categories__header">
        <div className="deleted-categories__info">
          <h3 className="deleted-categories__title">Danh mục đã xóa ({categories.length})</h3>
          <p className="deleted-categories__description">Danh sách các danh mục đã bị xóa và có thể khôi phục</p>
        </div>
        
        {categories.length > 0 && (
          <div className="deleted-categories__bulk-actions">
            <button 
              className="deleted-categories__bulk-btn deleted-categories__bulk-btn--restore"
              onClick={handleBulkRestore}
              disabled={selectedCategories.length === 0}
            >
              <RotateCcw size={16} />
              Khôi phục ({selectedCategories.length})
            </button>
            <button 
              className="deleted-categories__bulk-btn deleted-categories__bulk-btn--delete"
              onClick={handleBulkDelete}
              disabled={selectedCategories.length === 0}
            >
              <Trash2 size={16} />
              Xóa vĩnh viễn ({selectedCategories.length})
            </button>
            <button 
              className="deleted-categories__bulk-btn deleted-categories__bulk-btn--select"
              onClick={selectedCategories.length === categories.length ? deselectAll : selectAll}
            >
              {selectedCategories.length === categories.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
            </button>
          </div>
        )}
      </div>

      {categories.length === 0 ? (
        <div className="trash-empty">
          <Trash2 size={48} className="trash-empty__icon" />
          <h3 className="trash-empty__title">Không có danh mục nào trong thùng rác</h3>
          <p className="trash-empty__description">Tất cả danh mục đều đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="deleted-categories__list">
          {categories.map((category) => (
            <div key={category.id} className="deleted-categories__item">
              <div className="deleted-categories__checkbox">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => toggleSelectCategory(category.id)}
                />
              </div>
              
              <div className="deleted-categories__content">
                <div className="deleted-categories__item-header">
                  <h4 className="deleted-categories__item-name">{category.name}</h4>
                  <span className="deleted-categories__item-status deleted-categories__item-status--deleted">Đã xóa</span>
                </div>
                
                <div className="deleted-categories__meta">
                  <div className="deleted-categories__meta-item">
                    <User size={14} />
                    <span>Xóa bởi: {category.deletedBy}</span>
                  </div>
                  <div className="deleted-categories__meta-item">
                    <Calendar size={14} />
                    <span>Xóa lúc: {formatDateTime(category.deletedAt)}</span>
                  </div>
                  <div className="deleted-categories__meta-item">
                    <span>Số bài viết: {category.postCount}</span>
                  </div>
                </div>
                
                <div className="deleted-categories__description">
                  {category.description}
                </div>
              </div>
              
              <div className="deleted-categories__actions">
                <button 
                  className="deleted-categories__action-btn deleted-categories__action-btn--preview"
                  onClick={() => handleViewDetails(category)}
                  title="Xem chi tiết"
                >
                  <Eye size={16} />
                </button>
                <button 
                  className="deleted-categories__action-btn deleted-categories__action-btn--restore"
                  onClick={() => handleRestore(category.id)}
                  title="Khôi phục"
                >
                  <RotateCcw size={16} />
                </button>
                <button 
                  className="deleted-categories__action-btn deleted-categories__action-btn--delete"
                  onClick={() => handlePermanentDelete(category.id)}
                  title="Xóa vĩnh viễn"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {total > limit && (
        <div className="deleted-categories__pagination">
          <button 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="deleted-categories__pagination-btn"
          >
            Trước
          </button>
          <span className="deleted-categories__pagination-info">
            Trang {page} / {Math.ceil(total / limit)}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="deleted-categories__pagination-btn"
          >
            Sau
          </button>
        </div>
      )}
      
      {/* Detail Modal */}
      <TrashDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        item={selectedCategory}
        type="category"
      />
    </div>
  );
};

export default DeletedCategories;
